import { RefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import ProfilePicture from '@/components/ui/profile-picture';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { useFollowUserMutation } from '@/redux/follow/follow.api.slice';
import {
  IActivityActor,
  IActivityEntity,
  IActivityGroup,
  IActivityItem,
} from '@/redux/activity/activity.d';
import { shortAge } from './feedSections';
import {
  AVATAR_BADGE_TONE,
  CardKind,
  groupKind,
  groupPath,
  groupSentence,
  itemPath,
  itemSentence,
  SentenceParts,
  SYSTEM_TONE,
  VERB_ICON,
} from './activityPresentation';

type OnSeen = (id: string) => void;

function useReportWhenVisible(
  group: IActivityGroup,
  onSeen: OnSeen
): RefObject<HTMLLIElement> {
  const ref = useRef<HTMLLIElement>(null);
  const { key, seen } = group;
  const unseenIds = group.items
    .filter((item) => !item.seen)
    .map((item) => item._id)
    .join(',');
  useEffect(() => {
    const node = ref.current;
    if (seen || !node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        unseenIds.split(',').forEach(onSeen);
        observer.disconnect();
      },
      { threshold: 0.6 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [key, seen, unseenIds, onSeen]);
  return ref;
}

function VerbBadge({ verb }: { verb: CardKind }) {
  const tone = AVATAR_BADGE_TONE[verb];
  if (!tone) return null;
  const Icon = VERB_ICON[verb];
  return (
    <span
      className={cn(
        'absolute bottom-0 right-0 z-20 flex h-5 w-5 items-center justify-center rounded-full text-white ring-2 ring-background',
        tone
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
    </span>
  );
}

function SystemIcon({ verb }: { verb: CardKind }) {
  const Icon = VERB_ICON[verb];
  return (
    <span
      className={cn(
        'flex h-12 w-12 flex-none items-center justify-center rounded-full',
        SYSTEM_TONE[verb] ?? 'bg-muted text-muted-foreground'
      )}
    >
      <Icon className="h-6 w-6" aria-hidden="true" />
    </span>
  );
}

function ActorAvatar({
  actor,
  verb,
}: {
  actor: IActivityActor | null;
  verb: CardKind;
}) {
  if (!actor) return <SystemIcon verb={verb} />;
  return (
    <span className="relative flex-none">
      <ProfilePicture
        src={actor.profilePicture ?? undefined}
        alt={actor.username}
        username={actor.username}
        className="h-12 w-12"
      />
      <VerbBadge verb={verb} />
    </span>
  );
}

function SmallAvatar({
  actor,
  className,
}: {
  actor: IActivityActor;
  className: string;
}) {
  return (
    <ProfilePicture
      src={actor.profilePicture ?? undefined}
      alt={actor.username}
      className={className}
    />
  );
}

/** Two overlapping avatars for a group with more than one person in it. */
function GroupAvatar({ group }: { group: IActivityGroup }) {
  const [first, second] = group.actors;
  if (!second) {
    return <ActorAvatar actor={first ?? null} verb={groupKind(group)} />;
  }
  return (
    <span className="relative h-12 w-12 flex-none">
      <SmallAvatar actor={second} className="absolute right-0 top-0 h-8 w-8" />
      <SmallAvatar
        actor={first}
        className="absolute bottom-0 left-0 h-8 w-8 ring-2 ring-background"
      />
    </span>
  );
}

function Sentence({ parts, at }: { parts: SentenceParts; at: string }) {
  return (
    <p className="line-clamp-3 min-w-0 flex-1 text-sm leading-snug text-heading">
      {parts.actor && <span className="font-semibold">{parts.actor} </span>}
      {parts.before}
      {parts.title && (
        <span className="font-semibold">
          {parts.before && ' '}
          {parts.title}
        </span>
      )}
      {parts.after}{' '}
      <span className="text-muted-foreground">{shortAge(at)}</span>
    </p>
  );
}

function EntityVisual({
  entity,
  verb,
}: {
  entity: IActivityEntity | null;
  verb: CardKind;
}) {
  if (!entity) return null;
  if (entity.thumbnail) {
    return (
      <img
        src={entity.thumbnail}
        alt=""
        loading="lazy"
        className="h-12 w-12 flex-none rounded-md bg-muted object-cover"
      />
    );
  }
  const Icon = VERB_ICON[verb];
  return (
    <span className="flex h-12 w-12 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
  );
}

function FollowBackButton({ item }: { item: IActivityItem }) {
  const [followUser, { isLoading }] = useFollowUserMutation();
  const [followed, setFollowed] = useState(item.followingActor);
  if (followed) {
    return (
      <Button variant="secondary" size="sm" disabled className="flex-none">
        Following
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      className="flex-none"
      disabled={isLoading}
      onClick={async () => {
        if (!item.actor) return;
        await followUser(item.actor._id).unwrap();
        setFollowed(true);
      }}
    >
      Follow back
    </Button>
  );
}

const ROW = 'flex items-center gap-4 px-5 py-3.5 transition-colors';

function unseenTone(seen: boolean) {
  return seen ? 'hover:bg-muted/60' : 'bg-green-50/60 hover:bg-green-50';
}

function NewMarker({ seen }: { seen: boolean }) {
  if (seen) return null;
  return (
    <span
      className="h-2 w-2 flex-none rounded-full bg-green-600"
      aria-label="New"
    />
  );
}

/** One event: the whole row links to what happened. */
function ActivityRow({ item }: { item: IActivityItem }) {
  const { user } = useSelector(selectCurrentUser);
  return (
    <div className={cn(ROW, unseenTone(item.seen))}>
      <Link
        to={itemPath(item, user?.username ?? '')}
        className="flex min-w-0 flex-1 items-center gap-4"
      >
        <ActorAvatar actor={item.actor} verb={item.verb} />
        <Sentence parts={itemSentence(item)} at={item.createdAt} />
        <EntityVisual entity={item.entity} verb={item.verb} />
      </Link>
      {item.verb === 'FOLLOWED' && <FollowBackButton item={item} />}
      <NewMarker seen={item.seen} />
    </div>
  );
}

function GroupSummary({ group }: { group: IActivityGroup }) {
  const verb = groupKind(group);
  const body = (
    <>
      <GroupAvatar group={group} />
      <Sentence parts={groupSentence(group)} at={group.latestAt} />
      <EntityVisual entity={group.entity} verb={verb} />
    </>
  );
  const path = groupPath(group);
  if (!path) {
    return <div className="flex min-w-0 flex-1 items-center gap-4">{body}</div>;
  }
  return (
    <Link to={path} className="flex min-w-0 flex-1 items-center gap-4">
      {body}
    </Link>
  );
}

/** Several events on one thing: a summary that expands to each event. */
function ActivityGroupRow({ group }: { group: IActivityGroup }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <>
      <div className={cn(ROW, unseenTone(group.seen))}>
        <GroupSummary group={group} />
        <Button
          variant="ghost"
          size="icon"
          className="flex-none"
          aria-expanded={open}
          aria-label={open ? 'Hide each event' : 'Show each event'}
          onClick={() => setOpen(!open)}
        >
          <Chevron className="h-4 w-4" />
        </Button>
        <NewMarker seen={group.seen} />
      </div>
      {open && (
        <div className="border-l-2 border-border ml-10">
          {group.items.map((item) => (
            <ActivityRow key={item._id} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

function ActivityCard({
  group,
  onSeen,
}: {
  group: IActivityGroup;
  onSeen: OnSeen;
}) {
  const ref = useReportWhenVisible(group, onSeen);
  return (
    <li ref={ref} data-testid="activity-card">
      {group.items.length === 1 ? (
        <ActivityRow item={group.items[0]} />
      ) : (
        <ActivityGroupRow group={group} />
      )}
    </li>
  );
}

export default ActivityCard;
